import { DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { DragDropModule } from '@angular/cdk/drag-drop';
// Für das Zeilenmenü von app-table (cdkConnectedOverlay). Nebular stellt den
// CDK-OverlayContainer auf NbOverlayContainerAdapter um, das Overlay landet
// also innerhalb nb-layout — mit CI-Tokens und Overlay-Z-Index.
import { OverlayModule } from '@angular/cdk/overlay';

import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
  HttpRequest,
} from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  NB_AUTH_TOKEN_INTERCEPTOR_FILTER,
  NbAuthJWTInterceptor,
  NbAuthModule,
  NbAuthOAuth2JWTToken,
  NbPasswordAuthStrategy,
  NbTokenLocalStorage,
  NbTokenStorage,
} from '@nebular/auth';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import {
  NbAlertModule,
  NbAutocompleteModule,
  NbBadgeModule,
  NbButtonGroupModule,
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbContextMenuModule,
  NbDatepickerModule,
  NbDialogModule,
  NbFormFieldModule,
  NbGlobalPhysicalPosition,
  NbIconModule,
  NbInputModule,
  NbLayoutModule,
  NbListModule,
  NbMenuModule,
  NbPopoverModule,
  NbProgressBarModule,
  NbSelectModule,
  NbSidebarModule,
  NbSpinnerModule,
  NbRadioModule,
  NbStepperModule,
  NbTabsetModule,
  NbTagModule,
  NbThemeModule,
  NbToastrModule,
  NbToggleModule,
  NbTooltipModule,
  NbUserModule,
} from '@nebular/theme';
import { FileUploadModule } from 'ng2-file-upload';
import { NgxEditorModule } from 'ngx-editor';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DashboardHeaderComponent } from './components/dashboard-header/dashboard-header.component';
import { DashboardLayoutComponent } from './components/dashboard-layout/dashboard-layout.component';
import { GrainPulseComponent } from './components/grain-pulse/grain-pulse.component';
import { AuthTransitionComponent } from './components/auth-transition/auth-transition.component';
import { BucketDialogComponent } from './components/dialogs/bucket-dialog/bucket-dialog.component';
import { ConfirmDialogComponent } from './components/dialogs/confirm-dialog/confirm-dialog.component';
import { AdoptionModalComponent } from './components/dialogs/adoption-modal/adoption-modal.component';
import { OrbiDistributeDialogComponent } from './components/dialogs/orbi-distribute-dialog/orbi-distribute-dialog.component';
import { ExerciseCreateDialogComponent } from './components/dialogs/exercise-create-dialog/exercise-create-dialog.component';
import { ExerciseImportDialogComponent } from './components/dialogs/exercise-import-dialog/exercise-import-dialog.component';
import { CourseCreateDialogComponent } from './components/dialogs/course-create-dialog/course-create-dialog.component';
import { DispatchExerciseDialogComponent } from './components/dialogs/dispatch-exercise-dialog/dispatch-exercise-dialog.component';
import { TestExerciseDispatchDialogComponent } from './components/dialogs/test-exercise-dispatch-dialog/test-exercise-dispatch-dialog.component';
import { SubmissionReviewDialogComponent } from './components/dialogs/submission-review-dialog/submission-review-dialog.component';
import { MoodleCoursesDialogComponent } from './components/dialogs/moodle-courses-dialog/moodle-courses-dialog.component';
import { FaqDialogComponent } from './components/dialogs/faq-dialog/faq-dialog.component';
import { GuardrailRuleDialogComponent } from './components/dialogs/guardrail-rule-dialog/guardrail-rule-dialog.component';
import { GuardrailHitsDialogComponent } from './components/dialogs/guardrail-hits-dialog/guardrail-hits-dialog.component';
import { AlTableBadgeComponent } from './components/features/table-badge/table-badge.component';
import { AlTableTextComponent } from './components/features/table-text/table-text.component';
import { GuardrailsComponent } from './pages/guardrails/guardrails.component';
import { LearningsComponent } from './pages/learnings/learnings.component';
import { LearningDialogComponent } from './components/dialogs/learning-dialog/learning-dialog.component';
import { LearningDraftDialogComponent } from './components/dialogs/learning-draft-dialog/learning-draft-dialog.component';
import { McpConnectorDialogComponent } from './components/dialogs/mcp-connector-dialog/mcp-connector-dialog.component';
import { McpAcknowledgeDialogComponent } from './components/dialogs/mcp-acknowledge-dialog/mcp-acknowledge-dialog.component';
import { McpConnectorsComponent } from './pages/mcp-connectors/mcp-connectors.component';
import { McpConnectorDetailComponent } from './pages/mcp-connectors/mcp-connector-detail/mcp-connector-detail.component';
import { TutorMcpComponent } from './pages/tutors/tutor-id/mcp/tutor-mcp.component';
import { SkillsComponent } from './pages/skills/skills.component';
import { SkillEditorComponent } from './pages/skills/skill-editor/skill-editor.component';
import { SkillImportDialogComponent } from './components/dialogs/skill-import-dialog/skill-import-dialog.component';
import { SkillLibraryDialogComponent } from './components/dialogs/skill-library-dialog/skill-library-dialog.component';
import { LibrarySkillItemComponent } from './components/features/library-skill-item/library-skill-item.component';
import { TutorSkillsComponent } from './pages/tutors/tutor-id/skills/tutor-skills.component';
import { AgentSetupComponent } from './pages/agent-setup/agent-setup.component';
import { TutorLearningComponent } from './pages/tutors/tutor-id/learning/tutor-learning.component';
import { TutorKnowledgeComponent } from './pages/tutors/tutor-id/knowledge/tutor-knowledge.component';
import { TutorSetupComponent } from './pages/tutors/tutor-id/setup/tutor-setup.component';
import { TokenAnalyticsComponent } from './pages/token-analytics/token-analytics.component';
import { MarginReportComponent } from './pages/token-analytics/margin-report/margin-report.component';
import { AiRoutingComponent } from './pages/token-analytics/ai-routing/ai-routing.component';
import { CreditLimitsPanelComponent } from './components/credit-limits-panel/credit-limits-panel.component';
import { CreditAnalyticsComponent } from './pages/credit-analytics/credit-analytics.component';
import { FileUploadDialogComponent } from './components/dialogs/file-upload-dialog/file-upload-dialog.component';
import { AuditComponent } from './pages/admin/audit/audit.component';
import { ReferralsComponent } from './pages/admin/referrals/referrals.component';
import { ReferralAgreementDialogComponent } from './components/dialogs/referral-agreement-dialog/referral-agreement-dialog.component';
import { ReferralPartnerDialogComponent } from './components/dialogs/referral-partner-dialog/referral-partner-dialog.component';
import { LearningBlockCasesComponent } from './pages/admin/learning-block-cases/learning-block-cases.component';
import { PricingCalculatorComponent } from './pages/admin/pricing-calculator/pricing-calculator.component';
import { OfferStatusDialogComponent } from './pages/admin/pricing-calculator/offer-status-dialog/offer-status-dialog.component';
import { LearningBlockCaseDialogComponent } from './components/dialogs/learning-block-case-dialog/learning-block-case-dialog.component';
import { UsersComponent } from './pages/admin/users/users.component';
import { AdminLearnersComponent } from './pages/admin/learners/learners.component';
import { LearnerProfileDialogComponent } from './components/dialogs/learner-profile-dialog/learner-profile-dialog.component';
import { MoodleDashboardComponent } from './pages/moodle-dashboard/moodle-dashboard.component';
import { IliasDashboardComponent } from './pages/ilias-dashboard/ilias-dashboard.component';
import { MoodleDashboardCourseDetailComponent } from './pages/moodle-dashboard/detail-dialogs/course-detail.component';
import { ParsedCourseStructureDialogComponent } from './pages/moodle-dashboard/detail-dialogs/course-structure-dialog.component';
import { CourseDifficultyDialogComponent } from './pages/moodle-dashboard/detail-dialogs/course-difficulty-dialog.component';
import { MoodleDashboardUserDetailComponent } from './pages/moodle-dashboard/detail-dialogs/user-detail.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { ProductsComponent } from './pages/products/products.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { TutorsComponent } from './pages/tutors/tutors.component';
import { UsageComponent } from './pages/usage/usage.component';
import { AuthService } from './services/auth.service';

import { DeviceDetectorService } from 'ngx-device-detector';
import { ApiKeyDialogComponent } from './components/dialogs/api-key-dialog/api-key-dialog.component';
import { EmbeddingDialogComponent } from './components/dialogs/embedding-dialog/embedding-dialog.component';
import { TutorDialogComponent } from './components/dialogs/tutor-dialog/tutor-dialog.component';
import { TutorWizardComponent } from './components/dialogs/tutor-wizard/tutor-wizard.component';
import { LtiSetupWizardComponent } from './components/dialogs/lti-setup-wizard/lti-setup-wizard.component';
import { UserDialogComponent } from './components/dialogs/user-dialog/user-dialog.component';
import { AidStylePreviewComponent } from './pages/chat-styling/aid-style-preview/aid-style-preview.component';
import { ChatSettingsFormComponent } from './components/forms/chat-settings-form/chat-settings-form.component';
import { ColorInputComponent } from './components/color-input/color-input.component';
import { LanguageSelectComponent } from './components/language-select/language-select.component';
import { AgentOrbComponent } from './components/agent-orb/agent-orb.component';
import { OrbiLabComponent } from './components/orbi-lab/orbi-lab.component';
import { DozentLinkPickerComponent } from './components/dozent-link-picker/dozent-link-picker.component';
import { MoodleDozentAccessComponent } from './components/moodle-dozent-access/moodle-dozent-access.component';
import { TwinLinkedTutorsComponent } from './components/twin-linked-tutors/twin-linked-tutors.component';
import { LinkRequestDecisionDialogComponent } from './components/dialogs/link-request-decision-dialog/link-request-decision-dialog.component';
import { ImageUploadComponent } from './components/image-upload/image-upload.component';
import { AddressAutocompleteComponent } from './components/forms/address-autocomplete/address-autocomplete.component';
import { OrganisationAddressFormComponent } from './components/forms/organisation-address-form/organisation-address-form.component';
import { OrganisationGeneralFormComponent } from './components/forms/organisation-general-form/organisation-general-form.component';
import { TutorSettingsComponent } from './components/forms/tutor-settings/tutor-settings.component';
import { UserDetailsFormComponent } from './components/forms/user-details-form/user-details-form.component';
import { UserPasswordFormComponent } from './components/forms/user-password-form/user-password-form.component';
import { ProductCardComponent } from './components/product-card/product-card.component';
import { ErrorInterceptor } from './interceptors/error-interceptor.service';
import { OrganisationsComponent } from './pages/admin/organisations/organisations.component';
import { CourseIdComponent } from './pages/courses/course-id/course-id.component';
import { LessionsIdComponent } from './pages/courses/course-id/lessions/lessions-id/lessions-id.component';
import { CoursesComponent } from './pages/courses/courses.component';
import { CreatorStartComponent } from './pages/creator/creator-start/creator-start.component';
import { CreatorLibraryComponent } from './pages/creator/creator-library/creator-library.component';
import { CreatorAcCourseDialogComponent } from './components/dialogs/creator-ac-course-dialog/creator-ac-course-dialog.component';
import { CreatorMoodleDialogComponent } from './components/dialogs/creator-moodle-dialog/creator-moodle-dialog.component';
import { CreatorStartDialogComponent } from './components/dialogs/creator-start-dialog/creator-start-dialog.component';
import { CreatorOpenCellComponent } from './pages/creator/creator-library/creator-open-cell.component';
import { CreatorKbDialogComponent } from './components/dialogs/creator-kb-dialog/creator-kb-dialog.component';
import { CreatorTrashDialogComponent } from './components/dialogs/creator-trash-dialog/creator-trash-dialog.component';
import { CreatorRenameDialogComponent } from './components/dialogs/creator-rename-dialog/creator-rename-dialog.component';
import { CreatorImportDialogComponent } from './components/dialogs/creator-import-dialog/creator-import-dialog.component';
import { CurriculumComponent } from './pages/curriculum/curriculum.component';
import { CoursePrerequisitesComponent } from './components/course-prerequisites/course-prerequisites.component';
import { CoursePrerequisitesDialogComponent } from './components/dialogs/course-prerequisites-dialog/course-prerequisites-dialog.component';
import { PrerequisiteDecisionDialogComponent } from './components/dialogs/prerequisite-decision-dialog/prerequisite-decision-dialog.component';
import { CourseResponsiblesDialogComponent } from './components/dialogs/course-responsibles-dialog/course-responsibles-dialog.component';
import { ConversationsComponent } from './pages/analytics/conversations/conversations.component';
import { OrganisationComponent } from './pages/organisation/organisation.component';
import { DateAgoPipe } from './pipes/date-ago.pipe';

import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import {
  TranslateLoader,
  TranslateModule,
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as echarts from 'echarts/core';
import { NgxEchartsModule } from 'ngx-echarts';
import { TimeagoModule } from 'ngx-timeago';
import { BarAvgDurationComponent } from './components/charts/bar-avg-duration/bar-avg-duration.component';
import { BarRealtimeSessionsPerMinuteComponent } from './components/charts/bar-realtime-sessions-per-minute/bar-realtime-sessions-per-minute.component';
import { BarSessionsChartComponent } from './components/charts/bar-sessions-chart/bar-sessions-chart.component';
import { BarTotalDurationComponent } from './components/charts/bar-total-duration/bar-total-duration.component';
import { BarChartComponent } from './components/charts/basic/bar-chart/bar-chart.component';
import { DonutChartComponent } from './components/charts/basic/donut-chart/donut-chart.component';
import alphabeesTheme from './components/charts/basic/echart-theme.json';
import { GaugeChartComponent } from './components/charts/basic/gauge-chart/gauge-chart.component';
import { HeatmapChartComponent } from './components/charts/basic/heatmap-chart/heatmap-chart.component';
import { KpiCardComponent } from './components/charts/basic/kpi-card/kpi-card.component';
import { SankeyChartComponent } from './components/charts/basic/sankey-chart/sankey-chart.component';
import { SimpleStackedBarChartComponent } from './components/charts/basic/simple-stacked-bar-chart/simple-stacked-bar-chart.component';
import { ChartHeaderComponent } from './components/charts/chart-features/chart-header/chart-header.component';
import { DateRangeControlComponent } from './components/charts/chart-features/date-range-control/date-range-control.component';
import { AgentIdentityLabelComponent } from './components/charts/chart-features/agent-identity-label/agent-identity-label.component';
import { TutorFilterControlComponent } from './components/charts/chart-features/tutor-filter-control/tutor-filter-control.component';
import { DonutEventSplitComponent } from './components/charts/donut-event-split/donut-event-split.component';
import { GaugeRealtimeCurrentVsPeakUsersComponent } from './components/charts/gauge-realtime-current-vs-peak-users/gauge-realtime-current-vs-peak-users.component';
import { HbarEngagementRatePerEntityComponent } from './components/charts/hbar-engagement-rate-per-entity/hbar-engagement-rate-per-entity.component';
import { HbarEventCountsPerEntityComponent } from './components/charts/hbar-event-counts-per-entity/hbar-event-counts-per-entity.component';
import { HeatmapEventsComponent } from './components/charts/heatmap-events/heatmap-events.component';
import { HeatmapSessionsComponent } from './components/charts/heatmap-sessions/heatmap-sessions.component';
import { HstackThumbsPerEntityComponent } from './components/charts/hstack-thumbs-per-entity/hstack-thumbs-per-entity.component';
import { KpiEventsSummaryComponent } from './components/charts/kpi-events-summary/kpi-events-summary.component';
import { KpiSessionsSummaryComponent } from './components/charts/kpi-sessions-summary/kpi-sessions-summary.component';
import { LineAvgEventsPerSessionComponent } from './components/charts/line-avg-events-per-session/line-avg-events-per-session.component';
import { LineEventEngagementRateComponent } from './components/charts/line-event-engagement-rate/line-event-engagement-rate.component';
import { LineEventsExercisePerformanceComponent } from './components/charts/line-events-exercise-performance/line-events-exercise-performance.component';
import { LineSemanticsTimeSeriesBloomComponent } from './components/charts/line-semantics-time-series-bloom/line-semantics-time-series-bloom.component';
import { LineSemanticsTimeSeriesIntentComponent } from './components/charts/line-semantics-time-series-intent/line-semantics-time-series-intent.component';
import { LineSemanticsTimeSeriesTopicComponent } from './components/charts/line-semantics-time-series-topic/line-semantics-time-series-topic.component';
import { MultiLineEventTypesComponent } from './components/charts/multi-line-event-types/multi-line-event-types.component';
import { MultiLineSessionsAndAvgDurationsComponent } from './components/charts/multi-line-sessions-and-avg-durations/multi-line-sessions-and-avg-durations.component';
import { RealtimeFeedComponent } from './components/charts/realtime-feed/realtime-feed.component';
import { RealtimeKpisComponent } from './components/charts/realtime-kpis/realtime-kpis.component';
import { SankeySemanticsTopicsComponent } from './components/charts/sankey-semantics-topics/sankey-semantics-topics.component';
import { SemanticsDonutBloomComponent } from './components/charts/semantics-donut-bloom/semantics-donut-bloom.component';
import { SemanticsDonutIntentComponent } from './components/charts/semantics-donut-intent/semantics-donut-intent.component';
import { SemanticsDonutTopicComponent } from './components/charts/semantics-donut-topic/semantics-donut-topic.component';
import { SemanticsRankingTopCanonicalsComponent } from './components/charts/semantics-ranking-top-canonicals/semantics-ranking-top-canonicals.component';
import { SemanticsRankingTopTopicsComponent } from './components/charts/semantics-ranking-top-topics/semantics-ranking-top-topics.component';
import { CircleProgressComponent } from './components/circle-progress/circle-progress.component';
import { DemoBadgeComponent } from './components/demo-badge/demo-badge.component';
import { ChatPreviewDialogComponent } from './components/dialogs/chat-preview-dialog/chat-preview-dialog.component';
import { ChatSettingsDialogComponent } from './components/dialogs/chat-settings-dialog/chat-settings-dialog.component';
import { DomainWhitelistDialogComponent } from './components/dialogs/domain-whitelist-dialog/domain-whitelist-dialog.component';
import { EventFeedDetailsDialogComponent } from './components/dialogs/event-feed-details-dialog/event-feed-details-dialog.component';
import { ExtendTrialDialogComponent } from './components/dialogs/extend-trial-dialog/extend-trial-dialog.component';
import { ClientQuotaDialogComponent } from './components/dialogs/client-quota-dialog/client-quota-dialog.component';
import { BuyCreditsDialogComponent } from './components/dialogs/buy-credits-dialog/buy-credits-dialog.component';
import { PlanUpgradeDialogComponent } from './components/dialogs/plan-upgrade-dialog/plan-upgrade-dialog.component';
import { BillingDetailsDialogComponent } from './components/dialogs/billing-details-dialog/billing-details-dialog.component';
import { CreditsInfoDialogComponent } from './components/dialogs/credits-info-dialog/credits-info-dialog.component';
import { AutoRechargeDialogComponent } from './components/dialogs/auto-recharge-dialog/auto-recharge-dialog.component';
import { TokenUsageDialogComponent } from './components/dialogs/token-usage-dialog/token-usage-dialog.component';
import { InviteUserDialogComponent } from './components/dialogs/invite-user-dialog/invite-user-dialog.component';
import { FeatureSetDialogComponent } from './components/dialogs/feature-set-dialog/feature-set-dialog.component';
import { AvvDialogComponent } from './components/dialogs/avv-dialog/avv-dialog.component';
import { OrganisationDialogComponent } from './components/dialogs/organisation-dialog/organisation-dialog.component';
import { ContractEditorComponent } from './components/contract-editor/contract-editor.component';
import { ContractInvoicesComponent } from './components/contract-invoices/contract-invoices.component';
import { ManualInvoiceDialogComponent } from './components/dialogs/manual-invoice-dialog/manual-invoice-dialog.component';
import { RegistrationCompleteDialogComponent } from './components/dialogs/registration-complete-dialog/registration-complete-dialog.component';
import { WebsiteDialogComponent } from './components/dialogs/website-dialog/website-dialog.component';
import { MoodleImportDialogComponent } from './components/dialogs/moodle-import-dialog/moodle-import-dialog.component';
import { CourseStructureDialogComponent } from './components/dialogs/course-structure-dialog/course-structure-dialog.component';
import { CourseEditDialogComponent } from './components/dialogs/course-edit-dialog/course-edit-dialog.component';
import { MoodleConnectDialogComponent } from './components/dialogs/moodle-connect-dialog/moodle-connect-dialog.component';
import { MoodlePushDialogComponent } from './components/dialogs/moodle-push-dialog/moodle-push-dialog.component';
import { CloudConnectDialogComponent } from './components/dialogs/cloud-connect-dialog/cloud-connect-dialog.component';
import { CloudSyncDialogComponent } from './components/dialogs/cloud-sync-dialog/cloud-sync-dialog.component';
import { FilePreviewPanelComponent } from './components/panels/file-preview-panel/file-preview-panel.component';
import { FileRowActionsComponent } from './components/features/file-row-actions/file-row-actions.component';
import { FileIndexingStatusComponent } from './components/features/file-indexing-status/file-indexing-status.component';
import { IntegrationCardComponent } from './components/features/integration-card/integration-card.component';
import { PaginationComponent } from './components/features/pagination/pagination.component';
import { SearchInputComponent } from './components/features/search-input/search-input.component';
import { PageHelpComponent } from './components/page-help/page-help.component';
import { TableDynamicCellDirective } from './components/features/table/table-dynamic-cell.directive';
import { TableComponent } from './components/features/table/table.component';
import { AssistantSettingsFormComponent } from './components/forms/assistant-settings-form/assistant-settings-form.component';
import { KnowledgeSelectFormComponent } from './components/forms/knowledge-select-form/knowledge-select-form.component';
import { FaqListFormComponent } from './components/forms/faq-list-form/faq-list-form.component';
import { ModelSelectorComponent } from './components/forms/model-selector/model-selector.component';
import { InfoBarComponent } from './components/info-bar/info-bar.component';
import { PageLoaderComponent } from './components/page-loader/page-loader.component';
import { OrbiLoaderComponent } from './components/orbi-loader/orbi-loader.component';
import { OrbiTextDotComponent } from './components/orbi-text-dot/orbi-text-dot.component';
import { PortalHintsComponent } from './components/portal-hints/portal-hints.component';
import { UploadTrayComponent } from './components/upload-tray/upload-tray.component';
import { HintFilmComponent } from './components/portal-hints/hint-film.component';
import { AcademyVideoComponent } from './components/academy/academy-video.component';
import { AcademyFilmComponent } from './components/academy/academy-film.component';
import { AcademyQuizComponent } from './components/academy/academy-quiz.component';
import { AcademyProgressComponent } from './components/academy/academy-progress.component';
import { AcademyLessonItemComponent } from './components/academy/academy-lesson-item.component';
import { AcademyHomeComponent } from './pages/academy/academy-home/academy-home.component';
import { AcademyTopicComponent } from './pages/academy/academy-topic/academy-topic.component';
import { AcademyLessonComponent } from './pages/academy/academy-lesson/academy-lesson.component';
import { AcademyCertificateComponent } from './pages/academy/academy-certificate/academy-certificate.component';
import { FilesTableComponent } from './components/tables/files-table/files-table.component';
import { WebsiteTableComponent } from './components/tables/website-table/website-table.component';
import { CreatorItemsTableComponent } from './components/tables/creator-items-table/creator-items-table.component';
import { CoursesTableComponent } from './components/tables/courses-table/courses-table.component';
import { TurnstileComponent } from './components/turnstile/turnstile.component';
import { TutorCardComponent } from './components/tutor-card/tutor-card.component';
import { TutorMenuComponent } from './components/tutor-menu/tutor-menu.component';
import { StatusIconComponent } from './components/features/status-icon/status-icon.component';
import { NotesComponent } from './directives/notes/notes.component';
import { NotesDirective } from './directives/notes/notes.directive';
import { RequiredFieldFeedbackDirective } from './directives/required-field-feedback.directive';
import { MarqueeOnHoverDirective } from './directives/marquee-on-hover.directive';
import { TruncateTooltipDirective } from './directives/truncate-tooltip.directive';
import { ScrollOverflowDirective } from './directives/scroll-overflow.directive';
import { AgentFormDirective, AgentTargetDirective, AlAnchorDirective } from './agent/agent-anchors.directive';
import { AgentMarkdownPipe } from './agent/agent-markdown.pipe';
import { AgentPanelComponent } from './components/agent-panel/agent-panel.component';
import { AgentAccessPanelComponent } from './components/agent-access-panel/agent-access-panel.component';
import { CreatorConnectionsPanelComponent } from './components/creator-connections-panel/creator-connections-panel.component';
import { AgentModelPanelComponent } from './components/agent-model-panel/agent-model-panel.component';
import { OauthConsentComponent } from './pages/auth/oauth-consent/oauth-consent.component';
import { AgentTokenDialogComponent } from './components/dialogs/agent-token-dialog/agent-token-dialog.component';
import { AgentSpotlightComponent } from './components/agent-spotlight/agent-spotlight.component';
import { ConnectionsComponent } from './pages/admin/connections/connections.component';
import { CourseAnalyticsComponent } from './pages/analytics/course-analytics/course-analytics.component';
import { EventsComponent } from './pages/analytics/events/events.component';
import { LearnersAnalyticsComponent } from './pages/analytics/learners-analytics/learners-analytics.component';
import { LiveComponent } from './pages/analytics/live/live.component';
import { SemanticsComponent } from './pages/analytics/semantics/semantics.component';
import { SessionsComponent } from './pages/analytics/sessions/sessions.component';
import { ConfirmRegistrationComponent } from './pages/auth/confirm-registration/confirm-registration.component';
import { PasswordResetComponent } from './pages/auth/password-reset/password-reset.component';
import { PasswordRequestComponent } from './pages/auth/password-request/password-request.component';
import { ConfirmEmailChangeComponent } from './pages/auth/confirm-email-change/confirm-email-change.component';
import { NbLoginComponent } from './pages/auth/login/login.component';
import { TwoFactorSetupComponent } from './pages/auth/two-factor-setup/two-factor-setup.component';
import { TwoFactorFormComponent } from './components/forms/two-factor-form/two-factor-form.component';
import { NbRegisterComponent } from './pages/auth/register/register.component';
import { TemrsAndConditionsComponent } from './pages/auth/temrs-and-conditions/temrs-and-conditions.component';
import { MoodleSetupGuideComponent } from './pages/auth/moodle-setup-guide/moodle-setup-guide.component';
import { ChatStylingComponent } from './pages/chat-styling/chat-styling.component';
import { LiquidDemoComponent } from './pages/chat-styling/liquid-demo/liquid-demo.component';
import { IntegrationsComponent } from './pages/integrations/integrations.component';
import { KnowledgeBaseComponent } from './pages/knowledge-base/knowledge-base.component';
import { AiSettingsComponent } from './pages/tutors/tutor-id/ai-settings/ai-settings.component';
import { TutorShellComponent } from './pages/tutors/tutor-id/tutor-shell.component';
import { AccessComponent } from './pages/tutors/tutor-id/access/access.component';
import { LinkedTutorsComponent } from './pages/tutors/tutor-id/linked-tutors/linked-tutors.component';
import { PlacementsOverviewComponent } from './pages/tutors/placements/placements-overview.component';
import { PlacementsSiteCardComponent } from './components/placements-site-card/placements-site-card.component';
import { PlacementEditDialogComponent } from './components/dialogs/placement-edit-dialog/placement-edit-dialog.component';
import { LtiPlacementsCardComponent } from './components/lti-placements-card/lti-placements-card.component';
import { IliasPlacementsCardComponent } from './components/ilias-placements-card/ilias-placements-card.component';
import { LtiPlacementDialogComponent } from './components/dialogs/lti-placement-dialog/lti-placement-dialog.component';
import { LtiConnectDialogComponent } from './components/dialogs/lti-connect-dialog/lti-connect-dialog.component';
import { IliasConnectDialogComponent } from './components/dialogs/ilias-connect-dialog/ilias-connect-dialog.component';
import { PlacementCreateDialogComponent } from './components/dialogs/placement-create-dialog/placement-create-dialog.component';
import { TutorKbRowComponent } from './components/tutor-kb-row/tutor-kb-row.component';
import { LmsRegistrationsComponent } from './pages/tutors/lms-registrations/lms-registrations.component';
import { GeneralComponent } from './pages/tutors/tutor-id/general/general.component';
import { KnowledgeBaseComponent as TutorKnowledgeBaseComponent } from './pages/tutors/tutor-id/knowledge-base/knowledge-base.component';
import { SafePipe } from './pipes/safe.pipe';
import { ShortNumberPipe } from './pipes/short-number.pipe';
import { KnowledgeSourceNamePipe } from './pipes/knowledge-source-name.pipe';
import { InfoBadgeComponent } from './components/info-badge/info-badge.component';
import { DozentExercisesComponent } from './pages/dozent/exercises/exercises.component';
import { ExerciseEditorComponent } from './pages/dozent/exercises/exercise-editor/exercise-editor.component';
import { DozentParticipantsComponent } from './pages/dozent/participants/participants.component';
import { ParticipantDetailComponent } from './pages/dozent/participants/participant-detail/participant-detail.component';
import { CompetencyProfileComponent } from './components/competency-profile/competency-profile.component';
import { CompetencyRadarComponent } from './components/charts/basic/competency-radar/competency-radar.component';
import { LevelMeterComponent } from './components/level-meter/level-meter.component';
import { SegmentedControlComponent } from './components/segmented-control/segmented-control.component';
import { LearnerMemoryComponent } from './components/learner-memory/learner-memory.component';
import { ObjectiveRegisterComponent } from './components/objective-register/objective-register.component';
import { ParticipantDetailDialogComponent } from './components/dialogs/participant-detail-dialog/participant-detail-dialog.component';
import { DozentFaqsComponent } from './pages/dozent/faqs/faqs.component';
import { DozentMessagesComponent } from './pages/dozent/messages/messages.component';
import { DozentenComponent } from './pages/dozent/dozenten/dozenten.component';
import { DozentFilterDropdownComponent } from './components/dozent-filter-dropdown/dozent-filter-dropdown.component';

echarts.registerTheme('alphabees', alphabeesTheme);

registerLocaleData(localeDe, 'de-DE');

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    CoursePrerequisitesComponent,
    CoursePrerequisitesDialogComponent,
    PrerequisiteDecisionDialogComponent,
    CourseResponsiblesDialogComponent,
    ConversationsComponent,
    AppComponent,
    TutorsComponent,
    SettingsComponent,
    UsageComponent,
    NotFoundComponent,
    DashboardLayoutComponent,
    DashboardHeaderComponent,
    GrainPulseComponent,
    AuthTransitionComponent,
    UsersComponent,
    AuditComponent,
    ReferralsComponent,
    ReferralAgreementDialogComponent,
    ReferralPartnerDialogComponent,
    LearningBlockCasesComponent,
    PricingCalculatorComponent,
    OfferStatusDialogComponent,
    LearningBlockCaseDialogComponent,
    AdminLearnersComponent,
    LearnerProfileDialogComponent,
    MoodleDashboardComponent,
    IliasDashboardComponent,
    MoodleDashboardCourseDetailComponent,
    ParsedCourseStructureDialogComponent,
    CourseDifficultyDialogComponent,
    MoodleDashboardUserDetailComponent,
    ProductsComponent,
    BucketDialogComponent,
    ConfirmDialogComponent,
    AdoptionModalComponent,
    OrbiDistributeDialogComponent,
    ExerciseCreateDialogComponent,
    ExerciseImportDialogComponent,
    CourseCreateDialogComponent,
    DispatchExerciseDialogComponent,
    TestExerciseDispatchDialogComponent,
    SubmissionReviewDialogComponent,
    MoodleCoursesDialogComponent,
    FaqDialogComponent,
    GuardrailRuleDialogComponent,
    GuardrailHitsDialogComponent,
    AlTableBadgeComponent,
    AlTableTextComponent,
    GuardrailsComponent,
    LearningsComponent,
    LearningDialogComponent,
    LearningDraftDialogComponent,
    McpConnectorDialogComponent,
    McpAcknowledgeDialogComponent,
    McpConnectorsComponent,
    McpConnectorDetailComponent,
    TutorMcpComponent,
    SkillsComponent,
    SkillEditorComponent,
    SkillImportDialogComponent,
    SkillLibraryDialogComponent,
    LibrarySkillItemComponent,
    TutorSkillsComponent,
    AgentSetupComponent,
    TutorSetupComponent,
    TutorKnowledgeComponent,
    TutorLearningComponent,
    TokenAnalyticsComponent,
    CreditLimitsPanelComponent,
    MarginReportComponent,
    AiRoutingComponent,
    CreditAnalyticsComponent,
    FileUploadDialogComponent,
    OrganisationComponent,
    ConfirmRegistrationComponent,
    PasswordResetComponent,
    PasswordRequestComponent,
    ConfirmEmailChangeComponent,
    TutorDialogComponent,
    TutorWizardComponent,
    LtiSetupWizardComponent,
    TutorSettingsComponent,
    OrganisationsComponent,
    ProductCardComponent,
    DateAgoPipe,
    SafePipe,
    ShortNumberPipe,
    KnowledgeSourceNamePipe,
    OrganisationGeneralFormComponent,
    OrganisationAddressFormComponent,
    AddressAutocompleteComponent,
    UserDetailsFormComponent,
    UserPasswordFormComponent,
    UserDialogComponent,
    ApiKeyDialogComponent,
    EmbeddingDialogComponent,
    AidStylePreviewComponent,
    ChatSettingsFormComponent,
    ColorInputComponent,
    LanguageSelectComponent,
    AgentOrbComponent,
    OrbiLabComponent,
    DozentLinkPickerComponent,
    MoodleDozentAccessComponent,
    TwinLinkedTutorsComponent,
    LinkRequestDecisionDialogComponent,
    ImageUploadComponent,
    CoursesComponent,
    CreatorStartComponent,
    CreatorLibraryComponent,
    CreatorAcCourseDialogComponent,
    CreatorMoodleDialogComponent,
    CreatorStartDialogComponent,
    CreatorOpenCellComponent,
    CreatorKbDialogComponent,
    CreatorTrashDialogComponent,
    CreatorRenameDialogComponent,
    CreatorImportDialogComponent,
    CurriculumComponent,
    CourseIdComponent,
    LessionsIdComponent,
    InviteUserDialogComponent,
    FeatureSetDialogComponent,
    AvvDialogComponent,
    KnowledgeSelectFormComponent,
    FaqListFormComponent,
    ModelSelectorComponent,
    DomainWhitelistDialogComponent,
    ChatStylingComponent,
    LiquidDemoComponent,
    ChatPreviewDialogComponent,
    ChatSettingsDialogComponent,
    NbRegisterComponent,
    TemrsAndConditionsComponent,
    MoodleSetupGuideComponent,
    KnowledgeBaseComponent,
    WebsiteDialogComponent,
    AssistantSettingsFormComponent,
    TableComponent,
    TableDynamicCellDirective,
    StatusIconComponent,
    PaginationComponent,
    SearchInputComponent,
    PageHelpComponent,
    WebsiteTableComponent,
    CreatorItemsTableComponent,
    FilesTableComponent,
    CoursesTableComponent,
    MoodleImportDialogComponent,
    CourseStructureDialogComponent,
    CourseEditDialogComponent,
    MoodleConnectDialogComponent,
    MoodlePushDialogComponent,
    CloudConnectDialogComponent,
    CloudSyncDialogComponent,
    FilePreviewPanelComponent,
    FileRowActionsComponent,
    FileIndexingStatusComponent,
    ConnectionsComponent,
    NotesDirective,
    RequiredFieldFeedbackDirective,
    MarqueeOnHoverDirective,
    TruncateTooltipDirective,
    ScrollOverflowDirective,
    AlAnchorDirective,
    AgentTargetDirective,
    AgentFormDirective,
    AgentMarkdownPipe,
    AgentPanelComponent,
    AgentAccessPanelComponent,
    CreatorConnectionsPanelComponent,
    AgentModelPanelComponent,
    OauthConsentComponent,
    AgentTokenDialogComponent,
    AgentSpotlightComponent,
    NotesComponent,
    InfoBarComponent,
    IntegrationsComponent,
    IntegrationCardComponent,
    RegistrationCompleteDialogComponent,
    CourseAnalyticsComponent,
    LearnersAnalyticsComponent,
    PageLoaderComponent,
    OrbiLoaderComponent,
    OrbiTextDotComponent,
    PortalHintsComponent,
    UploadTrayComponent,
    HintFilmComponent,
    AcademyVideoComponent,
    AcademyFilmComponent,
    AcademyQuizComponent,
    AcademyProgressComponent,
    AcademyLessonItemComponent,
    AcademyHomeComponent,
    AcademyTopicComponent,
    AcademyLessonComponent,
    AcademyCertificateComponent,
    OrganisationDialogComponent,
    ContractEditorComponent,
    ContractInvoicesComponent,
    ManualInvoiceDialogComponent,
    TurnstileComponent,
    NbLoginComponent,
    TwoFactorSetupComponent,
    TwoFactorFormComponent,
    TutorCardComponent,
    GeneralComponent,
    AiSettingsComponent,
    TutorShellComponent,
    LinkedTutorsComponent,
    AccessComponent,
    PlacementsOverviewComponent,
    PlacementsSiteCardComponent,
    PlacementEditDialogComponent,
    LtiPlacementsCardComponent,
    IliasPlacementsCardComponent,
    LtiPlacementDialogComponent,
    LtiConnectDialogComponent,
    IliasConnectDialogComponent,
    PlacementCreateDialogComponent,
    TutorKbRowComponent,
    LmsRegistrationsComponent,
    TutorMenuComponent,
    TutorKnowledgeBaseComponent,
    LiveComponent,
    EventsComponent,
    SessionsComponent,
    MultiLineSessionsAndAvgDurationsComponent,
    DonutChartComponent,
    BarChartComponent,
    HeatmapChartComponent,
    SankeyChartComponent,
    BarSessionsChartComponent,
    BarTotalDurationComponent,
    BarAvgDurationComponent,
    KpiCardComponent,
    MultiLineEventTypesComponent,
    DonutEventSplitComponent,
    HbarEngagementRatePerEntityComponent,
    HbarEventCountsPerEntityComponent,
    HstackThumbsPerEntityComponent,
    HeatmapSessionsComponent,
    SemanticsComponent,
    KpiEventsSummaryComponent,
    KpiSessionsSummaryComponent,
    LineEventEngagementRateComponent,
    LineAvgEventsPerSessionComponent,
    LineEventsExercisePerformanceComponent,
    HeatmapEventsComponent,
    SankeySemanticsTopicsComponent,
    LineSemanticsTimeSeriesBloomComponent,
    LineSemanticsTimeSeriesTopicComponent,
    LineSemanticsTimeSeriesIntentComponent,
    BarRealtimeSessionsPerMinuteComponent,
    GaugeRealtimeCurrentVsPeakUsersComponent,
    GaugeChartComponent,
    RealtimeFeedComponent,
    RealtimeKpisComponent,
    DateRangeControlComponent,
    AgentIdentityLabelComponent,
    TutorFilterControlComponent,
    SimpleStackedBarChartComponent,
    ChartHeaderComponent,
    SemanticsRankingTopTopicsComponent,
    SemanticsRankingTopCanonicalsComponent,
    CircleProgressComponent,
    DemoBadgeComponent,
    EventFeedDetailsDialogComponent,
    SemanticsDonutBloomComponent,
    SemanticsDonutIntentComponent,
    SemanticsDonutTopicComponent,
    ExtendTrialDialogComponent,
    ClientQuotaDialogComponent,
    BuyCreditsDialogComponent,
    PlanUpgradeDialogComponent,
    BillingDetailsDialogComponent,
    CreditsInfoDialogComponent,
    AutoRechargeDialogComponent,
    TokenUsageDialogComponent,
    InfoBadgeComponent,
    DozentExercisesComponent,
    ExerciseEditorComponent,
    DozentParticipantsComponent,
    ParticipantDetailComponent,
    ParticipantDetailDialogComponent,
    CompetencyProfileComponent,
    CompetencyRadarComponent,
    LevelMeterComponent,
    SegmentedControlComponent,
    LearnerMemoryComponent,
    ObjectiveRegisterComponent,
    DozentFaqsComponent,
    DozentMessagesComponent,
    DozentenComponent,
  ],
  imports: [
    DozentFilterDropdownComponent,
    BrowserModule,
    DragDropModule,
    OverlayModule,
    AppRoutingModule,
    NbTabsetModule,
    BrowserAnimationsModule,
    NbThemeModule.forRoot({ name: 'alphabees' }), // corporate, default, cosmic, dark, light
    NbLayoutModule,
    NbStepperModule,
    NbSidebarModule.forRoot(),
    NbBadgeModule,
    NbButtonModule,
    NbEvaIconsModule,
    FormsModule,
    NbAlertModule,
    NbAutocompleteModule,
    NbCheckboxModule,
    NbDialogModule.forRoot({
      dialogClass: 'dialog',
    }),
    NbInputModule,
    TimeagoModule.forRoot(),
    NbToastrModule.forRoot({
      position: NbGlobalPhysicalPosition.BOTTOM_RIGHT,
      duration: 3000,
    }),
    ReactiveFormsModule,
    NbButtonModule,
    NbUserModule,
    FileUploadModule,
    NbFormFieldModule,
    NbContextMenuModule,
    NbCardModule,
    HttpClientModule,
    NbMenuModule.forRoot(),
    NbIconModule,
    NbProgressBarModule,
    NbSpinnerModule,
    NbSelectModule,
    NbRadioModule,
    NbTagModule,
    NbToggleModule,
    NbTooltipModule,
    NbButtonGroupModule,
    NbListModule,
    NbPopoverModule,
    NgxEditorModule,
    NbDatepickerModule.forRoot(),
    NbAuthModule.forRoot({
      strategies: [
        NbPasswordAuthStrategy.setup({
          name: 'email',
          baseEndpoint: environment.userBackend,
          login: {
            endpoint: '/token',
            redirect: {
              success: '/tutors',
              failure: null,
            },
            method: 'post',
          },
          token: {
            key: 'data',
            class: NbAuthOAuth2JWTToken,
          },
          register: {
            endpoint: '/register',
            method: 'post',
            requireValidToken: false,
            redirect: {
              success: '/auth/login',
              failure: '/auth/login',
            },
          },
          // logout: {
          //   endpoint: '/logout',
          //   redirect: {
          //     success: '/login',
          //     failure: null,
          //   },
          //   method: 'post',
          // },
          requestPass: {
            endpoint: '/password/reset',
            redirect: {
              success: '/auth/login',
              failure: null,
            },
            method: 'post',
          },
          resetPass: {
            endpoint: '/password/reset/confirm',
            redirect: {
              success: '/auth/login',
              failure: null,
            },
            method: 'post',
          },
          refreshToken: {
            endpoint: '/token/refresh',
            method: 'post',
          },

          errors: {
            key: 'detail',
          },
        }),
      ],
      forms: {
        validation: {
          password: {
            required: true,
            // 8 again, the value before 2026-09-21. NOTE: this block is
            // shared by every auth form. It must never exceed what existing
            // accounts already use — the login form reads the same config,
            // and raising it to 12 locked those accounts out.
            minLength: 8,
            maxLength: 50,
          },
        },
        register: {
          redirectDelay: 500,
          strategy: 'email',
          showMessages: {
            success: true,
            error: true,
          },
          terms: true,
          socialLinks: [],
        },
        resetPass: {
          redirectDelay: 2000,
          strategy: 'email',
        },
        requestPass: {
          redirectDelay: 2000,
          strategy: 'email',
        },
      },
    }),

    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    NgxEchartsModule.forRoot({ echarts: () => import('echarts') }),
  ],
  providers: [
    AuthService,
    TranslatePipe,
    DeviceDetectorService,
    { provide: NbTokenStorage, useClass: NbTokenLocalStorage },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NbAuthJWTInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: NB_AUTH_TOKEN_INTERCEPTOR_FILTER,
      useValue: function (req: HttpRequest<any>) {
        if (
          req.url.includes('/token/refresh') ||
          req.url.includes('/register') ||
          req.url.includes('/auth/confirm-registration') ||
          req.url.includes('/auth/login')
        ) {
          return true;
        }
        return false;
      },
    },
    {
      provide: LOCALE_ID,
      useValue: navigator.language.startsWith('en') ? 'en-US' : 'de-DE',
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR',
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(translate: TranslateService) {
    console.log('al-portal is production:', environment.production);
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang('en');
    var userLang = (
      localStorage.getItem('language') ?? navigator.language
    ).slice(0, 2);

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    if (supportedLanguages.includes(userLang)) {
      translate.use(userLang);
    } else {
      translate.use('en');
    }
  }
}

export const supportedLanguages = ['en', 'de'];
